import { requestAsync, mutateAsync, httpMethods } from 'redux-query';
import _ from 'lodash';
import moment from 'moment-timezone';
import cfg from 'cfg';
import auth from 'dash-front/auth';
import { createApiFetch } from 'dash-front/actions/helpers';
import { STORE_KEYS, CAMPAIGN_FORM_KEYS } from 'dash-front/constants';
import { getEntities } from 'dash-front/selectors';
import { fetchWidgetSpecs } from 'dash-front/actions/widget-specs';
const WIDGET_KEY = CAMPAIGN_FORM_KEYS.WIDGET;
const TIME_FORMAT = 'HH:mm';
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

const transformConditionFromServer = (condition, allValues) => {
    if (!_.isObject(condition)) {
        return condition;
    }
    return Object.keys(condition).reduce((acc, cur) => {
        const { timezone } = allValues;

        switch (cur) {
            case 'time': {
                const utcTime = condition[cur];
                const userTime = moment
                    .utc(utcTime, TIME_FORMAT)
                    .tz(timezone)
                    .format(TIME_FORMAT);
                acc[cur] = userTime;
                break;
            }
            case 'dateTime': {
                const utcTime = condition[cur];
                const userTime = moment
                    .utc(utcTime, DATE_TIME_FORMAT)
                    .tz(timezone)
                    .format(DATE_TIME_FORMAT);
                acc[cur] = userTime;
                break;
            }
            default:
                acc[cur] = condition[cur];
        }
        return acc;
    }, {});
};

const getDate = (date, timezone) =>
    date
        ? moment(
              moment(date)
                  .tz(timezone)
                  .format('YYYY-MM-DDTHH:mm:ss')
          ).toDate()
        : null;

/**
 * Is used to map a campaign to REDUX FORM structure.
 * @param {*} campaign
 */
const updateCampaignFieldsToStruct = campaign => {
    const { timezone } = campaign;
    campaign.startDate = getDate(campaign.startDate, timezone);
    campaign.endDate = getDate(campaign.endDate, timezone);
    campaign.messengerNotifications = campaign.notificationsTo.reduce((prev, current) => {
        if (current.type === 'messenger') {
            prev.push({
                userName: current.userName,
                userPhoto: current.userPhoto,
                userRef: current.value,
                userId: current.userId,
                value: current.value
            });
        }
        return prev;
    }, []);
    campaign.notificationsTo = campaign.notificationsTo.filter(n => n.type !== 'messenger');
    (campaign.rules || []).forEach(rule => {
        const rest = _.omit(rule.presentation.params, ['theme', 'template', 'client', 'user', 'username']);
        rule.presentation.additional = [];
        rule.conditions = rule.conditions.map(condition => transformConditionFromServer(condition, campaign));
        Object.keys(rest).forEach(key => {
            rule.presentation.additional.push({
                key,
                [WIDGET_KEY.PARAMS.ADDITIONAL.VALUE]: 'manual',
                [WIDGET_KEY.PARAMS.ADDITIONAL.CUSTOM_VALUE]: rest[key],
                [WIDGET_KEY.PARAMS.ADDITIONAL.BY_TRIGGER]: false
            });
        });
        const triggerParams = rule.presentation[WIDGET_KEY.TRIGGER_PARAMS.SECTION] || {};
        Object.keys(triggerParams).forEach(key => {
            rule.presentation.additional.push({
                key,
                [WIDGET_KEY.PARAMS.ADDITIONAL.VALUE]: triggerParams[key],
                [WIDGET_KEY.PARAMS.ADDITIONAL.BY_TRIGGER]: true
            });
        });
    });
    return campaign;
};

const mapCampaignFromServerData = data => {
    const { campaign } = data;
    return updateCampaignFieldsToStruct(campaign);
};

const transformCampaigns = data => {
    const { campaigns } = data;
    return {
        [STORE_KEYS.CAMPAIGNS]: campaigns.reduce((acc, cur) => {
            acc[cur._id] = updateCampaignFieldsToStruct(cur);
            return acc;
        }, {})
    };
};

const updateCampaigns = {
    [STORE_KEYS.CAMPAIGNS]: (prev, cur) => {
        return { ...prev, ...cur };
    }
};

export const campaignsFetchConfig = () => ({
    url: `${cfg.campaign_manager.host}/api/campaigns?access_token=${auth.getAccessToken()}`,
    force: true,
    queryKey: 'GET:campaigns',
    transform: transformCampaigns,
    update: updateCampaigns
});

export const fetchCampaigns = () => requestAsync(campaignsFetchConfig());

const transformCampaignById = widgets => data => {
    const campaign = mapCampaignFromServerData(data);
    _.forEach(campaign.rules, rule => {
        const widgetId = _.get(rule, 'presentation.name');
        const widget = widgets.find(el => el.id === widgetId);
        const additional = rule.presentation.additional;
        if (widget) {
            const propTypes = _.get(widget, 'spec.propTypes', []);
            const newProps = propTypes.filter(
                prop => prop.key !== 'template' && !additional.some(add => add.key === prop.key)
            );
            newProps.forEach(prop => {
                additional.push({
                    key: prop.key,
                    value: 'manual',
                    customValue: prop.defaultValue,
                    byTrigger: false
                });
            });
        }
    });
    return {
        [STORE_KEYS.CAMPAIGNS]: { [campaign._id]: campaign }
    };
};

const getWidgetSpecs = (dispatch, getState) => {
    const entities = getEntities(getState());
    const prevWidgetSpecs = entities[STORE_KEYS.WIDGET_SPECS];
    return prevWidgetSpecs && Array.isArray(prevWidgetSpecs.widgets)
        ? Promise.resolve(prevWidgetSpecs.widgets)
        : dispatch(fetchWidgetSpecs()).then(({ entities }) => entities[STORE_KEYS.WIDGET_SPECS].widgets);
};

export const fetchCampaignById = campaignId => dispatch => {
    return dispatch(getWidgetSpecs).then(widgets => {
        const url = `${cfg.campaign_manager.host}/api/campaigns/${campaignId}?access_token=${auth.getAccessToken()}`;
        const requestConfig = createApiFetch({
            url,
            key: STORE_KEYS.CAMPAIGNS,
            configOptions: {
                transform: transformCampaignById(widgets),
                update: {
                    [STORE_KEYS.CAMPAIGNS]: (prev, curr) => {
                        return { ...prev, ...curr };
                    }
                }
            }
        });
        return dispatch(requestAsync(requestConfig));
    });
};

export const deleteCampaign = (id, config = {}) => {
    const url = `${cfg.campaign_manager.host}/api/campaigns/${id}?access_token=${auth.getAccessToken()}`;
    const { options, ...restConfig } = config;
    const requestConfig = {
        url,
        update: {
            [STORE_KEYS.CAMPAIGNS]: campaigns => {
                if (!campaigns) {
                    return campaigns;
                }
                // eslint-disable-next-line
                const { [id]: toDel, ...rest } = campaigns;
                return rest;
            }
        },
        queryKey: STORE_KEYS.CAMPAIGNS,
        options: {
            ...options,
            method: httpMethods.DELETE
        },
        ...restConfig
    };
    return mutateAsync(requestConfig);
};

export const updateCampaign = campaign => {
    const { _id } = campaign;
    const url = `${cfg.campaign_manager.host}/api/campaigns/${_id}?access_token=${auth.getAccessToken()}`;
    const config = {
        body: campaign,
        url,
        queryKey: STORE_KEYS.CAMPAIGNS,
        key: STORE_KEYS.CAMPAIGNS,
        force: true,
        transform: response => {
            const campaign = mapCampaignFromServerData(response);
            return {
                [STORE_KEYS.CAMPAIGNS]: {
                    [campaign._id]: campaign
                }
            };
        },
        update: {
            [STORE_KEYS.CAMPAIGNS]: (prev, cur) => {
                return { ...prev, ...cur };
            }
        },
        options: {
            method: httpMethods.PUT
        }
    };
    return requestAsync(config);
};



/** WEBPACK FOOTER **
 ** ../src/dash/front/campaign/actions/campaigns.js
 **/